import { AppError } from '../errors/app-error';
import type { AssetMetadata, ContentRegistry } from './content-registry';

export type LoadingState = 'IDLE' | 'LOADING' | 'READY' | 'FAILED';
export interface ContentLoadSnapshot { readonly state: LoadingState; readonly attempt: number; readonly error: AppError | null; readonly registry: ContentRegistry | null }
export interface LoaderLogger { warn(message: string, detail?: unknown): void }
export type AssetProbe = (asset: AssetMetadata) => Promise<void>;

export class ContentLoader {
  private state: LoadingState = 'IDLE'; private attempt = 0; private error: AppError | null = null; private registry: ContentRegistry | null = null;
  private readonly listeners = new Set<(snapshot: ContentLoadSnapshot) => void>();
  public constructor(private readonly registryFactory: () => ContentRegistry, private readonly probe: AssetProbe, private readonly logger: LoaderLogger) {}
  public getSnapshot(): ContentLoadSnapshot { return Object.freeze({ state: this.state, attempt: this.attempt, error: this.error, registry: this.registry }); }
  public subscribe(listener: (snapshot: ContentLoadSnapshot) => void): () => void { this.listeners.add(listener); return () => this.listeners.delete(listener); }
  public async load(): Promise<ContentLoadSnapshot> {
    if (this.state === 'LOADING') return this.getSnapshot();
    this.state = 'LOADING'; this.attempt += 1; this.error = null; this.registry = null; this.notify();
    try {
      const registry = this.registryFactory();
      for (const asset of registry.getAssets()) {
        try { await this.probe(asset); }
        catch (error) {
          if (asset.required) throw new AppError('ASSET_LOAD_ERROR', `Required asset failed: ${asset.id}`, true, { cause: error });
          this.logger.warn(`Optional asset failed: ${asset.id}`, error);
        }
      }
      this.registry = registry; this.state = 'READY';
    } catch (error) {
      this.error = error instanceof AppError ? error : new AppError('CONTENT_ERROR', 'Content validation failed.', true, { cause: error });
      this.state = 'FAILED';
    }
    this.notify(); return this.getSnapshot();
  }
  public retry(): Promise<ContentLoadSnapshot> {
    if (this.state !== 'FAILED') return Promise.resolve(this.getSnapshot());
    return this.load();
  }
  private notify(): void { const snapshot = this.getSnapshot(); for (const listener of [...this.listeners]) listener(snapshot); }
}

export const fetchAssetProbe: AssetProbe = async (asset) => {
  const response = await fetch(asset.url); if (!response.ok) throw new Error(`HTTP ${response.status}`);
};
